// User Service - Handles user management API calls

import { apiGet, apiPost, apiPatch, apiDelete, apiPostMultipart } from '@/utils/api';

export interface StaffCampus {
  id: string;
  name: string;
  city: string;
  isPrimary: boolean;
}

export interface StaffUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  phoneCountry?: string;
  role: string;
  status: string;
  staffCode?: string; // Unique staff code (e.g., DG250001, PROF2500001)
  profileSubmissionStatus?: 'DRAFT' | 'PENDING_VALIDATION' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  lastLoginAt?: string;
  campuses?: StaffCampus[];
  academicYear?: {
    id: string;
    name: string;
    isCurrent: boolean;
  };
  // Professor/E-DEP fields
  isAlsoProfessor?: boolean;
  statutProfesseur?: string;
  specialite?: string;
  matieresEnseignees?: string;
  tauxHoraire?: string;
  etablissementOrigine?: string;
  rib?: string;
  grade?: {
    id: string;
    labelFr: string;
  };
  departementAttache?: {
    id: string;
    labelFr: string;
  };
  diplome?: {
    id: string;
    labelFr: string;
  };
}

export interface LinkedParent {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  phoneCountry: string | null;
  relationship: string;
  isPrimary: boolean;
  status: 'ACTIVE' | 'PENDING' | 'INACTIVE' | 'SUSPENDED';
  username: string | null;
  tempPassword: string | null;
  mustChangePassword: boolean;
}

export interface EmergencyContact {
  name: string | null;
  phone: string | null;
  relation: string | null;
}

export interface StudentCampus {
  id: string;
  name: string;
  city: string;
}

export interface StudentUser {
  id: string;
  email: string | null;
  username?: string | null; // Legacy username (usually null for students)
  studentCode?: string | null; // Student code (e.g., IMMT250001) — login identifier
  firstName: string;
  lastName: string;
  phone?: string;
  phoneCountry?: string;
  role: string;
  status: string;
  profileSubmissionStatus?: 'DRAFT' | 'PENDING_VALIDATION' | 'APPROVED' | 'REJECTED';
  emailVerified: boolean;
  mustChangePassword?: boolean;
  tempPassword?: string | null; // Plain text temp password for admin to share (only for students without email)
  createdAt: string;
  lastLoginAt?: string | null; // Null if user has never logged in - used to show credentials key icon
  // Enhanced data for students
  studentId?: string | null;
  linkedParents?: LinkedParent[];
  emergencyContact?: EmergencyContact | null;
  campus?: StudentCampus | null;
  academicYear?: {
    id: string;
    name: string;
    isCurrent: boolean;
  };
  // Program info for filtering
  program?: {
    id: string;
    code: string;
    name: string;
    durationYears: number;
  } | null;
  currentSemester?: number | null;
  // Academic info for filtering
  codeFiliereInscrit?: string | null;
  filiereInscrit?: string | null;
  diplomeInscrit?: string | null;
  niveauFormation?: number | null;
  // Active group assignments (for filtering and exports by group)
  groupes?: Array<{ id: string; name: string }>;
}

// Filter parameters for student list
export interface StudentFilters {
  search?: string;
  codeFiliereInscrit?: string[];
  diplomeInscrit?: string[];
  niveauFormation?: number[];
  academicYearId?: string[];
  campusId?: string[];
  profileSubmissionStatus?: string[];
  includeWithdrawn?: boolean;
  page?: number;
  limit?: number;
}

// Filter parameters for staff list
export interface StaffFilters {
  search?: string;
  role?: string[];
  academicYearId?: string[];
  campusId?: string[];
  hasTeachingActivities?: boolean; // If true, returns users with role=TEACHER OR isAlsoProfessor=true
  page?: number;
  limit?: number;
}

// Pagination meta information
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  /** Student list only: whole-scope totals, independent of the page */
  pendingValidationTotal?: number;
  withdrawnTotal?: number;
}

// Paginated response structure from backend
export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface ManagementPermissions {
  canManageUsers: boolean;
  canManageStaff: boolean;
  canManageStudents: boolean;
  canCreateStaff: boolean;      // Permission-based: can create non-teacher staff
  canCreateTeacher: boolean;    // Permission-based: can create teachers (separate permission)
  canCreateStudent: boolean;    // Permission-based: can create students
  creatableRoles: Array<{
    role: string;
    nameFr: string;
    nameEn: string;
    level: number;
  }>;
  currentRole: string;
  currentRoleInfo: {
    nameFr: string;
    nameEn: string;
  };
  currentUserCampusIds?: string[];
}

/**
 * DTO for creating a staff user account
 * Mirrors the structure seen in student creation but adapted for staff profiles
 * Only firstName, lastName, email, and role are required - all others are optional
 */
export interface CreateStaffUserDto {
  // === REQUIRED FIELDS ===
  email: string;
  firstName: string;
  lastName: string;
  role: string;

  // === OPTIONAL CONTACT FIELDS ===
  phone?: string;
  phoneCountry?: string;
  adresse?: string;
  ville?: string;
  codePostal?: string;

  // === OPTIONAL IDENTITY FIELDS ===
  prenomArabe?: string;
  nomArabe?: string;
  cnie?: string;
  passport?: string;
  sexe?: 'M' | 'F';
  dateNaissance?: string;
  paysOrigine?: string;
  handicap?: string;

  // === OPTIONAL EMERGENCY CONTACT ===
  urgenceNom?: string;
  urgenceNumero?: string;
  urgenceAdresse?: string;
  urgenceRelation?: string;
  urgencePhoneCountry?: string;

  // === OPTIONAL PROFESSIONAL FIELDS (for professors/teachers) ===
  dateRecrutement?: string;
  fonction?: string;
  diplomeId?: string;
  specialite?: string;
  matieresEnseignees?: string;
  statutProfesseur?: string;
  tauxHoraire?: string;
  gradeId?: string;
  estOriginaireEtablissementPublic?: string;
  etablissementOrigine?: string;
  departementAttacheId?: string;
  rib?: string;
  salaireMensuelNet?: string;

  // === ALSO PROFESSOR FLAG (for staff who also teach) ===
  isAlsoProfessor?: boolean;

  // === CAMPUS ASSIGNMENT ===
  campusIds?: string[];

  // === PAYROLL CAMPUS (campus responsible for payment) ===
  payrollCampusId?: string;

  // === ACADEMIC YEAR ASSIGNMENT ===
  academicYearId?: string;

  // === PHYSICAL DOCUMENT TRACKING ===
  physicalDocCin?: boolean;
  physicalDocPassport?: boolean;
  physicalDocCv?: boolean;
  physicalDocContratTravail?: boolean;
  physicalDocDiplome?: boolean;
  physicalDocAttestationRib?: boolean;
  physicalDocEngagementProfesseur?: boolean;
  physicalDocReglementInterieur?: boolean;
  physicalDocConsentement?: boolean;
}

/**
 * DTO for creating a student user account
 * Uses consistent French nomenclature matching the Student model and E-DEP requirements
 *
 * Note: Email is optional. If not provided, the student will use their student code
 * (e.g., ETU10001) as username with a formatted temp password (LastName@123456)
 */
export interface CreateStudentUserDto {
  // === REQUIRED FIELDS (French nomenclature) ===
  prenom: string;                    // Prenom (firstName)
  nom: string;                       // Nom de famille (lastName)
  email?: string;                    // Adresse email (optional - if not provided, uses student code login)
  telephone: string;                 // Numero de telephone
  codeFiliereInscrit: string;        // Code filiere d'inscription
  niveauFormation: number;           // Niveau d'inscription (1-5)
  includeInEdep: boolean;            // Inclure dans E-DEP

  // === OPTIONAL IDENTITY FIELDS ===
  prenomArabe?: string;              // Prenom en arabe
  nomArabe?: string;                 // Nom en arabe
  cnie?: string;                     // Numero CNIE
  passport?: string;                 // Numero de passeport
  sexe?: 'M' | 'F';                  // Sexe (M/F)
  dateNaissance?: string;            // Date de naissance (DD/MM/YYYY ou YYYY-MM-DD)
  lieuNaissance?: string;            // Lieu de naissance
  paysOrigine?: string;              // Code pays d'origine
  handicap?: string;                 // Code handicap

  // === OPTIONAL CONTACT FIELDS ===
  phoneCountry?: string;             // Code pays du telephone
  adresse?: string;                  // Adresse complete
  ville?: string;                    // Ville
  codePostal?: string;               // Code postal

  // === OPTIONAL ACADEMIC FIELDS ===
  numeroMassarCNE?: string;          // Numero Massar/CNE
  situation?: 'N' | 'R' | 'P';       // Situation (N=Nouveau, R=Redoublant, P=Promu)
  premiereAnneeInscription?: string; // Premiere annee d'inscription

  // === OPTIONAL BAC/DIPLOMA FIELDS ===
  serieBaccalaureat?: string;        // Serie du baccalaureat
  anneeObtentionBac?: string;        // Annee d'obtention du bac
  diplomeAcces?: string;             // Diplome d'acces
  specialiteDiplomeAcces?: string;   // Specialite du diplome d'acces
  mentionDiplomeAcces?: 'p' | 'ab' | 'b' | 'tb' | 'e' | 'ns'; // Mention
  lieuObtentionDiplomeAcces?: string; // Lieu d'obtention

  // === OPTIONAL STATUS FIELDS ===
  resident?: 'O' | 'N';              // Resident (hebergement universitaire)
  fonctionnaire?: 'O' | 'N';         // Fonctionnaire
  pourcentageBourse?: number;        // Pourcentage de bourse (0-100)
  mobilite?: 'O' | 'N';              // Programme de mobilite

  // === OPTIONAL EMERGENCY CONTACT ===
  urgenceNom?: string;               // Nom du contact d'urgence
  urgenceNumero?: string;            // Telephone du contact d'urgence
  urgenceAdresse?: string;           // Adresse du contact d'urgence
  urgenceRelation?: string;          // Relation avec le contact
  urgencePhoneCountry?: string;      // Code pays du telephone d'urgence

  // === CAMPUS AFFILIATION ===
  campusId?: string;                 // ID du campus (obligatoire si plusieurs campus)

  // === ACADEMIC YEAR ASSIGNMENT ===
  academicYearId?: string;           // ID de l'annee academique

  // === AUTO-VALIDATION ===
  autoValidate?: boolean;            // Auto-valider le profil si complet

  // === FINANCIAL FIELDS (for account creation) ===
  typeFormation?: 'INITIALE' | 'CONTINUE';  // Type de formation
  selectedAncillaryFeeIds?: string[];       // IDs des frais annexes selectionnes
  selectedDiscountIds?: string[];           // IDs des remises/bourses selectionnees (seront en attente de validation)
  selectedPaymentScheduleId?: string;       // ID de l'echeancier de paiement selectionne

  // === PHYSICAL DOCUMENT TRACKING (Student-specific) ===
  physicalDocStudentCin?: boolean;          // CIN/Passport copy received
  physicalDocStudentBac?: boolean;          // BAC certificate received
  physicalDocStudentReleveBac?: boolean;    // BAC transcript received
  physicalDocStudentDernierDiplome?: boolean; // Last degree received (for non-BAC entry)
  physicalDocStudentReleveDiplome?: boolean;  // Last degree transcript received
  physicalDocStudentReleveAnneesPrecedentes?: boolean; // Previous years transcripts received
  physicalDocStudentCv?: boolean;           // CV received (optional)
  physicalDocStudentLettreMotivation?: boolean; // Motivation letter received (optional)
}

// Response type for student creation - includes credentials when no email
export interface StudentCredentials {
  username: string;     // Student code (e.g., ETU10001)
  tempPassword: string; // Formatted password (e.g., Harkat@123456)
}

export interface CreateStudentResponse {
  user: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string;
    lastName: string;
    phone: string;
    role: string;
    status: string;
    createdAt: string;
  };
  student: {
    id: string;
    studentCode: string;
    codeFiliereInscrit: string;
    filiereInscrit: string;
    niveauFormation: number;
    includeInEdep: boolean;
  };
  // Only present when student is created without email
  credentials?: StudentCredentials;
}

export interface StudentLink {
  studentId: string;
  relationship: 'FATHER' | 'MOTHER' | 'GUARDIAN' | 'STEPFATHER' | 'STEPMOTHER' | 'GRANDPARENT' | 'SIBLING' | 'OTHER';
  isPrimary?: boolean;
}

export interface CreateParentUserDto {
  // Required fields (same as parent profile form)
  firstName: string;
  lastName: string;
  phone: string;
  sexe: string;  // Required - M or F
  adresse: string;  // Required - min 5 characters
  studentLinks: StudentLink[];
  // Optional email (parents may not have email)
  email?: string;
  // Optional identity fields
  firstNameArabic?: string;
  lastNameArabic?: string;
  cnie?: string;
  passportNumber?: string;
  dateNaissance?: string;
  paysOrigine?: string;
  // Optional contact fields
  ville?: string;
  codePostal?: string;
  phoneCountry?: string;
  // Emergency contact
  urgenceNom?: string;
  urgenceNumero?: string;
  urgenceAdresse?: string;
  urgenceRelation?: string;
  urgencePhoneCountry?: string;
  // Relation with student
  relationAvecEtudiant?: string;
  relationPrecision?: string;
}

export interface ParentCredentials {
  username: string;
  tempPassword: string;
}

export interface CreateParentResponse {
  user: {
    id: string;
    email: string | null;
    username: string;
    firstName: string;
    lastName: string;
    phone: string;
    role: string;
    status: string;
    mustChangePassword: boolean;
    createdAt: string;
  };
  linkedStudents: StudentLink[];
  credentials: ParentCredentials;
}

export interface StudentForLinking {
  id: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  email: string;
  filiereInscrit?: string;
  niveauFormation?: number;
}

export interface ParentRelationType {
  value: 'FATHER' | 'MOTHER' | 'GUARDIAN' | 'STEPFATHER' | 'STEPMOTHER' | 'GRANDPARENT' | 'SIBLING' | 'OTHER';
  label: string;
}

export const PARENT_RELATION_TYPES: ParentRelationType[] = [
  { value: 'FATHER', label: 'Pere' },
  { value: 'MOTHER', label: 'Mere' },
  { value: 'GUARDIAN', label: 'Tuteur legal' },
  { value: 'STEPFATHER', label: 'Beau-pere' },
  { value: 'STEPMOTHER', label: 'Belle-mere' },
  { value: 'GRANDPARENT', label: 'Grand-parent' },
  { value: 'SIBLING', label: 'Frere/Soeur' },
  { value: 'OTHER', label: 'Autre' },
];

// Campus types for dropdown
export interface CampusOption {
  id: string;
  name: string;
  city: string;
  isMainCampus: boolean;
}

export interface ActiveCampusesResponse {
  campuses: CampusOption[];
  count: number;
  requiresCampusSelection: boolean;
}

// Bulk import types
export interface BulkImportStudentResult {
  rowNumber: number;
  success: boolean;
  email: string;
  nom: string;
  prenom: string;
  error?: string;
  userId?: string;
  studentId?: string;
}

export interface BulkImportSkippedDuplicate {
  rowNumber: number;
  email: string;
  nom: string;
  prenom: string;
  reason: string;
}

export interface BulkImportRejectedEmail {
  rowNumber: number;
  nom: string;
  prenom: string;
}

export interface BulkImportResponse {
  totalRows: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  results: BulkImportStudentResult[];
  skippedDuplicates?: BulkImportSkippedDuplicate[];
  rejectedMissingEmail?: BulkImportRejectedEmail[];
}

export interface BulkImportOptions {
  defaultCampusId?: string;
  academicYearId?: string;
  includeInEdep?: boolean;
  // Initiale/Continue for rows without a « Type de formation » column
  defaultTypeFormation?: 'INITIALE' | 'CONTINUE';
}

// Professor bulk import types
export interface BulkImportProfessorResult {
  rowNumber: number;
  success: boolean;
  email: string;
  nom: string;
  prenom: string;
  error?: string;
  userId?: string;
}

export interface BulkImportProfessorRejected {
  rowNumber: number;
  nom: string;
  prenom: string;
  reason: string;
}

export interface BulkImportProfessorsResponse {
  totalRows: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  results: BulkImportProfessorResult[];
  skippedDuplicates: BulkImportSkippedDuplicate[];
  rejectedMissingRequired: BulkImportProfessorRejected[];
}

export interface BulkImportProfessorsOptions {
  campusIds?: string[];
  academicYearId?: string;
}

/**
 * Fetch management permissions for the current user
 */
export async function fetchManagementPermissions(): Promise<ManagementPermissions> {
  return apiGet<ManagementPermissions>('/users/management/permissions');
}

/**
 * Fetch all staff users (filtered by permissions)
 * Supports advanced filters for role, annee academique, and campus
 * Returns paginated response with data and meta
 */
export async function fetchStaffUsers(filters?: StaffFilters): Promise<PaginatedResponse<StaffUser>> {
  // Build query string from filters
  const params = new URLSearchParams();

  if (filters?.search) {
    params.set('search', filters.search);
  }
  if (filters?.role && filters.role.length > 0) {
    params.set('role', filters.role.join(','));
  }
  if (filters?.academicYearId && filters.academicYearId.length > 0) {
    params.set('academicYearId', filters.academicYearId.join(','));
  }
  if (filters?.campusId && filters.campusId.length > 0) {
    params.set('campusId', filters.campusId.join(','));
  }
  if (filters?.hasTeachingActivities) {
    params.set('hasTeachingActivities', 'true');
  }
  // Pagination parameters
  if (filters?.page) {
    params.set('page', String(filters.page));
  }
  if (filters?.limit) {
    params.set('limit', String(filters.limit));
  }

  const queryString = params.toString();
  const url = queryString ? `/users/staff?${queryString}` : '/users/staff';

  return apiGet<PaginatedResponse<StaffUser>>(url);
}

/**
 * Staff dashboard statistics
 */
export interface StaffStats {
  totalStaff: number;
  totalProfessors: number;
  totalCoordinators: number;
  pendingValidation: number;
  byRole: Array<{
    role: string;
    count: number;
    nameFr: string;
  }>;
}

/**
 * Fetch staff dashboard statistics
 */
export async function fetchStaffStats(): Promise<StaffStats> {
  return apiGet<StaffStats>('/users/staff/stats');
}

/**
 * Fetch all student users (filtered by permissions)
 * Supports advanced filters for filiere, diplome, niveau, annee academique, campus
 * Returns paginated response with data and meta
 */
export async function fetchStudentUsers(filters?: StudentFilters): Promise<PaginatedResponse<StudentUser>> {
  // Build query string from filters
  const params = new URLSearchParams();

  if (filters?.search) {
    params.set('search', filters.search);
  }
  if (filters?.codeFiliereInscrit && filters.codeFiliereInscrit.length > 0) {
    params.set('codeFiliereInscrit', filters.codeFiliereInscrit.join(','));
  }
  if (filters?.diplomeInscrit && filters.diplomeInscrit.length > 0) {
    params.set('diplomeInscrit', filters.diplomeInscrit.join(','));
  }
  if (filters?.niveauFormation && filters.niveauFormation.length > 0) {
    params.set('niveauFormation', filters.niveauFormation.join(','));
  }
  if (filters?.academicYearId && filters.academicYearId.length > 0) {
    params.set('academicYearId', filters.academicYearId.join(','));
  }
  if (filters?.campusId && filters.campusId.length > 0) {
    params.set('campusId', filters.campusId.join(','));
  }
  if (filters?.profileSubmissionStatus && filters.profileSubmissionStatus.length > 0) {
    params.set('profileSubmissionStatus', filters.profileSubmissionStatus.join(','));
  }
  if (filters?.includeWithdrawn !== undefined) {
    params.set('includeWithdrawn', String(filters.includeWithdrawn));
  }
  // Pagination parameters
  if (filters?.page) {
    params.set('page', String(filters.page));
  }
  if (filters?.limit) {
    params.set('limit', String(filters.limit));
  }

  const queryString = params.toString();
  const url = queryString ? `/users/students?${queryString}` : '/users/students';

  return apiGet<PaginatedResponse<StudentUser>>(url);
}

/**
 * Headcount per academic year, read from the enrolment history: a year keeps
 * the students who studied it even after they were promoted or graduated, so
 * the counts overlap across years by design.
 */
export async function getStudentCountsByAcademicYear(): Promise<
  { academicYearId: string; count: number }[]
> {
  return apiGet<{ academicYearId: string; count: number }[]>('/users/students/year-counts');
}

/**
 * Headcount per academic year for the staff list. Counts what the list shows:
 * an active employee appears whatever year is selected, the year only narrows
 * down archived staff.
 */
export async function getStaffCountsByAcademicYear(): Promise<
  { academicYearId: string; count: number }[]
> {
  return apiGet<{ academicYearId: string; count: number }[]>('/users/staff/year-counts');
}

/**
 * Fetch ALL student users by walking every page of the paginated endpoint.
 * The API caps each page at 100 rows, so exports and filter options must
 * aggregate pages instead of relying on a single request.
 */
export async function fetchAllStudentUsers(
  filters?: Omit<StudentFilters, 'page' | 'limit'>,
): Promise<StudentUser[]> {
  const PAGE_LIMIT = 100;
  const all: StudentUser[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await fetchStudentUsers({ ...filters, page, limit: PAGE_LIMIT });
    all.push(...(response?.data || []));
    totalPages = response?.meta?.totalPages ?? 1;
    page += 1;
  } while (page <= totalPages);

  return all;
}

/**
 * Fetch active campuses for dropdown selection
 * Returns list of active campuses and whether campus selection is required
 */
export async function fetchActiveCampuses(): Promise<ActiveCampusesResponse> {
  return apiGet<ActiveCampusesResponse>('/users/campuses/active');
}

/**
 * Create a new staff user
 */
export async function createStaffUser(data: CreateStaffUserDto): Promise<StaffUser> {
  return apiPost<StaffUser>('/users/staff', data);
}

/**
 * Create a new student user
 * If email is not provided, the response will include credentials (username + temp password)
 */
export async function createStudentUser(data: CreateStudentUserDto): Promise<CreateStudentResponse> {
  return apiPost<CreateStudentResponse>('/users/students', data);
}

/**
 * Fetch students for parent linking dropdown
 */
export async function fetchStudentsForLinking(): Promise<StudentForLinking[]> {
  return apiGet<StudentForLinking[]>('/users/students/for-linking');
}

/**
 * Create a new parent user
 * Returns the user, linked students, and credentials (username + temp password)
 */
export async function createParentUser(data: CreateParentUserDto): Promise<CreateParentResponse> {
  return apiPost<CreateParentResponse>('/users/parents', data);
}

/**
 * Get parents linked to a student
 */
export async function fetchStudentParents(studentId: string): Promise<Array<{ id: string; relationship: string; isPrimary: boolean; parent: StudentUser }>> {
  return apiGet<Array<{ id: string; relationship: string; isPrimary: boolean; parent: StudentUser }>>(`/users/students/${studentId}/parents`);
}

/**
 * Get students linked to a parent
 */
export async function fetchParentStudents(parentUserId: string): Promise<Array<{ relationship: string; isPrimary: boolean; student: StudentForLinking }>> {
  return apiGet<Array<{ relationship: string; isPrimary: boolean; student: StudentForLinking }>>(`/users/parents/${parentUserId}/students`);
}

/**
 * Resend verification email to a student or parent
 */
export async function resendVerificationEmail(userId: string): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(`/users/${userId}/resend-verification`);
}

export interface DeadlineStatus {
  isPastDeadline: boolean;
  deadlineDate: string | null;
  daysRemaining: number | null;
  firstLoginAt: string | null;
  isBlocked: boolean;
  hasSubmittedProfile: boolean;
}

/**
 * Restart a user's profile submission deadline (new window starts now, soft-block lifted)
 */
export async function resetProfileDeadline(userId: string): Promise<DeadlineStatus> {
  return apiPost<DeadlineStatus>(`/settings/user/${userId}/reset-profile-deadline`);
}

/**
 * Document types for student documents
 */
export type StudentDocumentType =
  | 'CIN_FRONT'
  | 'CIN_BACK'
  | 'PASSPORT'
  | 'DIPLOME_BAC_SCAN'
  | 'RELEVES_NOTES_BAC'
  | 'DIPLOME_ACCES_SCAN'
  | 'RELEVES_NOTES'
  | 'CV'
  | 'LETTRE_MOTIVATION'
  | 'PHOTO';

/**
 * Document types for staff documents (matching DocumentUpload component)
 */
export type StaffDocumentType =
  | 'CIN_FRONT'
  | 'CIN_BACK'
  | 'PASSPORT'
  | 'PHOTO'
  | 'CV'
  | 'CONTRAT_TRAVAIL'
  | 'DIPLOME_SCAN'
  | 'ATTESTATION_RIB'
  | 'AUTORISATION_ENSEIGNER'
  | 'ENGAGEMENT_PROFESSEUR';

/**
 * Staff document type configuration with labels
 */
export interface StaffDocumentTypeConfig {
  type: StaffDocumentType;
  label: string;
  category: 'identity' | 'professional' | 'professor';
  required?: boolean;
  professorOnly?: boolean;
  vacataireOnly?: boolean;
  fromPublicInstitutionOnly?: boolean;
}

export const STAFF_DOCUMENT_TYPES: StaffDocumentTypeConfig[] = [
  // Identity documents
  { type: 'CIN_FRONT', label: 'CIN (Recto)', category: 'identity' },
  { type: 'CIN_BACK', label: 'CIN (Verso)', category: 'identity' },
  { type: 'PASSPORT', label: 'Passeport', category: 'identity' },
  { type: 'PHOTO', label: 'Photo d\'identite', category: 'identity' },
  // Professional documents
  { type: 'CV', label: 'Curriculum Vitae', category: 'professional' },
  { type: 'CONTRAT_TRAVAIL', label: 'Contrat de travail', category: 'professional' },
  { type: 'DIPLOME_SCAN', label: 'Diplome (scan)', category: 'professional' },
  { type: 'ATTESTATION_RIB', label: 'Attestation RIB', category: 'professional' },
  // Professor-specific documents (E-DEP)
  { type: 'AUTORISATION_ENSEIGNER', label: 'Autorisation d\'enseigner', category: 'professor', professorOnly: true, vacataireOnly: true, fromPublicInstitutionOnly: true },
  { type: 'ENGAGEMENT_PROFESSEUR', label: 'Engagement professeur', category: 'professor', professorOnly: true },
];

/**
 * Upload a document to a student's profile (staff use)
 */
export async function uploadStudentDocument(
  studentId: string,
  documentType: StudentDocumentType,
  file: File
): Promise<{ id: string; documentType: string; fileName: string; fileUrl: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);

  return apiPostMultipart<{ id: string; documentType: string; fileName: string; fileUrl: string }>(
    `/users/students/${studentId}/documents/upload`,
    formData
  );
}

/**
 * Upload a document for a staff member
 */
export async function uploadStaffDocument(
  userId: string,
  documentType: StaffDocumentType,
  file: File
): Promise<{ id: string; documentType: string; fileName: string; fileUrl: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);

  return apiPostMultipart<{ id: string; documentType: string; fileName: string; fileUrl: string }>(
    `/users/staff/${userId}/documents/upload`,
    formData
  );
}

/**
 * Response type for staff creation with documents
 */
export interface CreateStaffResponse {
  user: StaffUser;
  emailVerifyToken?: string;
}

/**
 * Create a staff user and optionally upload documents
 * Documents are uploaded after the user is created
 */
export async function createStaffUserWithDocuments(
  data: CreateStaffUserDto,
  documents: Array<{ type: StaffDocumentType; file: File }>
): Promise<{ user: StaffUser; uploadedDocuments: Array<{ type: string; success: boolean; error?: string }> }> {
  // First create the staff user
  const result = await apiPost<CreateStaffResponse>('/users/staff', data);

  // If no documents to upload, return early
  if (!documents || documents.length === 0) {
    return { user: result.user, uploadedDocuments: [] };
  }

  // Upload documents for the newly created staff member
  const uploadResults = await Promise.all(
    documents.map(async (doc) => {
      try {
        await uploadStaffDocument(result.user.id, doc.type, doc.file);
        return { type: doc.type, success: true };
      } catch (error) {
        return {
          type: doc.type,
          success: false,
          error: error instanceof Error ? error.message : 'Erreur inconnue'
        };
      }
    })
  );

  return { user: result.user, uploadedDocuments: uploadResults };
}

/**
 * Bulk import students from an Excel file
 */
export async function bulkImportStudents(
  file: File,
  options: BulkImportOptions = {}
): Promise<BulkImportResponse> {
  const formData = new FormData();
  formData.append('file', file);

  if (options.defaultCampusId) {
    formData.append('defaultCampusId', options.defaultCampusId);
  }
  if (options.academicYearId) {
    formData.append('academicYearId', options.academicYearId);
  }
  if (options.includeInEdep !== undefined) {
    formData.append('includeInEdep', String(options.includeInEdep));
  }
  if (options.defaultTypeFormation) {
    formData.append('defaultTypeFormation', options.defaultTypeFormation);
  }

  return apiPostMultipart<BulkImportResponse>('/users/students/bulk-import', formData);
}

/**
 * Bulk import professors from an Excel file
 */
export async function bulkImportProfessors(
  file: File,
  options: BulkImportProfessorsOptions = {}
): Promise<BulkImportProfessorsResponse> {
  const formData = new FormData();
  formData.append('file', file);

  if (options.campusIds && options.campusIds.length > 0) {
    formData.append('campusIds', options.campusIds.join(','));
  }
  if (options.academicYearId) {
    formData.append('academicYearId', options.academicYearId);
  }

  return apiPostMultipart<BulkImportProfessorsResponse>('/users/staff/professors/bulk-import', formData);
}

// ==================== ROLE MANAGEMENT ====================

/**
 * Role permission info for editing a user's role
 */
export interface RolePermissionInfo {
  canEdit: boolean;
  assignableRoles: Array<{
    role: string;
    nameFr: string;
    nameEn: string;
    level: number;
  }>;
  currentRole: string;
  currentRoleInfo: {
    nameFr: string;
    nameEn: string;
  };
}

/**
 * Check if the current user can edit another user's role
 * Returns whether edit is allowed and what roles can be assigned
 */
export async function fetchStaffRolePermissions(userId: string): Promise<RolePermissionInfo> {
  return apiGet<RolePermissionInfo>(`/users/staff/${userId}/role-permissions`);
}

/**
 * Update a staff member's role
 * Only hierarchical superiors (n+1) can change a user's role
 * DG can also change their own role
 */
export async function updateStaffRole(
  userId: string,
  newRole: string
): Promise<{ success: boolean; user: { id: string; role: string; firstName: string; lastName: string }; clearedOverrides: number }> {
  return apiPatch<{ success: boolean; user: { id: string; role: string; firstName: string; lastName: string }; clearedOverrides: number }>(
    `/users/staff/${userId}/role`,
    { role: newRole }
  );
}

// ==================== STUDENT ARCHIVE (DESISTEMENT) ====================

/**
 * Archive a student (mark as desistement/withdrawn)
 * Sets the user status to WITHDRAWN
 */
export async function archiveStudent(userId: string): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(`/users/students/${userId}/archive`);
}

/**
 * Restore a withdrawn student (unarchive)
 * Sets the user status back to ACTIVE
 */
export async function restoreStudent(userId: string): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(`/users/students/${userId}/restore`);
}

/**
 * Permanently delete a student
 * WARNING: This action is irreversible
 */
export async function deleteStudent(userId: string): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(`/users/students/${userId}`);
}

/**
 * Permanently delete a staff member
 * WARNING: This action is irreversible
 */
export async function deleteStaff(userId: string): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(`/users/staff/${userId}`);
}

// ==================== STAFF ARCHIVE (DEMISSION) ====================

/**
 * Archive a staff member (mark as demission/withdrawn)
 * Sets the user status to WITHDRAWN
 */
export async function archiveStaff(userId: string): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(`/users/staff/${userId}/archive`);
}

/**
 * Restore an archived staff member (unarchive)
 * Sets the user status back to ACTIVE
 */
export async function restoreStaff(userId: string): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(`/users/staff/${userId}/restore`);
}

// ==================== STAFF SUSPENSION (SUSPENDRE/ACTIVER) ====================

export interface SuspendStaffResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    status: string;
    suspendedAt: string;
    suspensionReason: string;
  };
}

export interface UnsuspendStaffResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    status: string;
  };
}

export interface StaffSuspensionInfo {
  isSuspended: boolean;
  suspendedAt: string | null;
  suspensionReason: string | null;
  suspendedBy: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

/**
 * Suspend a staff member account
 * Sets the user status to SUSPENDED and stores the reason
 * Requires STAFF_SUSPEND_ACTIVATE permission
 */
export async function suspendStaff(userId: string, reason: string): Promise<SuspendStaffResponse> {
  return apiPost<SuspendStaffResponse>(`/users/staff/${userId}/suspend`, { reason });
}

/**
 * Unsuspend/activate a staff member account
 * Sets the user status back to ACTIVE and clears suspension info
 * Requires STAFF_SUSPEND_ACTIVATE permission
 */
export async function unsuspendStaff(userId: string, notes?: string): Promise<UnsuspendStaffResponse> {
  return apiPost<UnsuspendStaffResponse>(`/users/staff/${userId}/unsuspend`, { notes });
}

/**
 * Get suspension info for a staff member
 */
export async function getStaffSuspensionInfo(userId: string): Promise<StaffSuspensionInfo> {
  return apiGet<StaffSuspensionInfo>(`/users/staff/${userId}/suspension-info`);
}

/**
 * Admin reset password for a user
 * Sends a password reset email to the user
 * Requires STUDENT_RESET_PASSWORD permission for students/parents
 * Requires STAFF_RESET_PASSWORD permission for staff members
 */
export async function adminResetPassword(userId: string): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(`/users/${userId}/admin-reset-password`);
}

export interface AdminResetParentPasswordResponse {
  success: boolean;
  message: string;
  firstName: string;
  lastName: string;
  username: string | null;
  tempPassword: string;
}

/**
 * Admin reset password for a parent with temporary password
 * Creates a temporary password that the parent must change on first login
 * Requires STUDENT_RESET_PASSWORD permission
 */
export async function adminResetParentPassword(parentUserId: string): Promise<AdminResetParentPasswordResponse> {
  return apiPost<AdminResetParentPasswordResponse>(`/users/${parentUserId}/admin-reset-parent-password`);
}

export interface AdminResetStudentTempPasswordResponse {
  success: boolean;
  message: string;
  firstName: string;
  lastName: string;
  email: string | null;
  tempPassword: string;
}

/**
 * Admin reset password for a student with temporary password
 * Creates a temporary password that the student must change on first login
 * Requires STUDENT_RESET_PASSWORD permission
 */
export async function adminResetStudentTempPassword(studentUserId: string): Promise<AdminResetStudentTempPasswordResponse> {
  return apiPost<AdminResetStudentTempPasswordResponse>(`/users/${studentUserId}/admin-reset-student-temp-password`);
}

export interface AdminResetStaffTempPasswordResponse {
  success: boolean;
  message: string;
  firstName: string;
  lastName: string;
  email: string | null;
  tempPassword: string;
}

/**
 * Admin reset password for a staff member with temporary password
 * Creates a temporary password that the staff member must change on first login
 * Requires STAFF_RESET_PASSWORD permission
 */
export async function adminResetStaffTempPassword(staffUserId: string): Promise<AdminResetStaffTempPasswordResponse> {
  return apiPost<AdminResetStaffTempPasswordResponse>(`/users/${staffUserId}/admin-reset-staff-temp-password`);
}

// ==================== STUDENT SUSPENSION (HOLDS) ====================

export type HoldType = 'ACADEMIC' | 'DISCIPLINARY' | 'FINANCIAL';

export interface SuspendStudentRequest {
  holdType: HoldType;
  reason?: string;
}

export interface SuspendStudentResponse {
  success: boolean;
  message: string;
  hold: {
    id: string;
    holdType: HoldType;
    reason: string | null;
    placedAt: string;
  };
}

export interface UnsuspendStudentResponse {
  success: boolean;
  message: string;
  holdsReleased: number;
}

export interface StudentHold {
  id: string;
  holdType: HoldType;
  reason: string | null;
  placedAt: string;
  placedBy: { firstName: string; lastName: string };
}

export interface ReleasedStudentHold extends StudentHold {
  releasedAt: string | null;
  releasedBy: { firstName: string; lastName: string } | null;
}

export interface StudentHoldsResponse {
  activeHolds: StudentHold[];
  releasedHolds: ReleasedStudentHold[];
}

export interface ReleaseHoldResponse {
  success: boolean;
  message: string;
  studentReactivated: boolean;
}

/**
 * Suspend a student by placing a hold on their account
 * Requires STUDENT_SUSPEND_ACTIVATE permission
 */
export async function suspendStudent(
  userId: string,
  data: SuspendStudentRequest
): Promise<SuspendStudentResponse> {
  return apiPost<SuspendStudentResponse>(`/users/students/${userId}/suspend`, data);
}

/**
 * Unsuspend a student by releasing all active holds
 * Requires STUDENT_SUSPEND_ACTIVATE permission
 */
export async function unsuspendStudent(userId: string): Promise<UnsuspendStudentResponse> {
  return apiPost<UnsuspendStudentResponse>(`/users/students/${userId}/unsuspend`);
}

/**
 * Get all holds (active and released) for a student
 */
export async function getStudentHolds(userId: string): Promise<StudentHoldsResponse> {
  return apiGet<StudentHoldsResponse>(`/users/students/${userId}/holds`);
}

/**
 * Release a specific hold on a student account
 * Requires STUDENT_SUSPEND_ACTIVATE permission
 */
export async function releaseHold(
  holdId: string,
  releaseNotes?: string
): Promise<ReleaseHoldResponse> {
  return apiPost<ReleaseHoldResponse>(`/users/holds/${holdId}/release`, { releaseNotes });
}
